import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  name?: string;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  resetKey?: unknown;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };
  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const tag = this.props.name ? `[ErrorBoundary:${this.props.name}]` : '[ErrorBoundary]';
    console.error(tag, error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <div className="error-boundary" role="alert" data-testid="error-boundary">
        <div className="error-boundary-content">
          <div className="error-boundary-icon" aria-hidden="true">⚠️</div>
          <h2 className="error-boundary-title">Something went wrong</h2>
          <p className="error-boundary-message">
            {error.message || 'An unexpected error occurred while rendering this view.'}
          </p>
          <div className="error-boundary-actions">
            <button
              type="button"
              className="error-boundary-btn primary"
              onClick={this.reset}
            >
              Try again
            </button>
            <button
              type="button"
              className="error-boundary-btn"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
